/**
 * usePriceAlertEngine — subscribes to price updates and fires price alerts.
 * Uses subscribeWithSelector so it never triggers re-renders.
 * Call once from DataLayer in App.tsx.
 */

import { useEffect } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { playAlertTone } from './useAlerts.js';

export function usePriceAlertEngine(): void {
  useEffect(() => {
    const unsub = useTerminalStore.subscribe(
      state => state.prices,
      (prices) => {
        const state   = useTerminalStore.getState();
        const active  = state.priceAlerts.filter(a => a.firedAt === undefined);
        if (active.length === 0) return;

        for (const alert of active) {
          const price = prices[alert.symbol]?.priceUsd;
          if (price === undefined) continue;

          const triggered =
            alert.condition === 'above' ? price >= alert.targetPrice
                                        : price <= alert.targetPrice;

          if (!triggered) continue;

          state.firePriceAlert(alert.id);
          playAlertTone();

          // Browser notification
          if (Notification.permission === 'granted') {
            try {
              const emoji = alert.condition === 'above' ? '🔔' : '🔕';
              new Notification(
                `${emoji} Price Alert — ${alert.symbol}`,
                {
                  body: `${alert.symbol} is ${alert.condition} $${alert.targetPrice.toFixed(4)} · Now: $${price.toFixed(4)}`,
                  icon: '/favicon.ico',
                  tag:  `arc-alert-${alert.id}`,
                }
              );
            } catch {
              // Notification API not available
            }
          }
        }
      }
    );

    return unsub;
  }, []);
}
